import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, createSlots as _createSlots, resolveDirective as _resolveDirective, withDirectives as _withDirectives, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "text-xl font-semibold text-gray-800" }
const _hoisted_2 = { class: "flex items-center space-x-4" }
const _hoisted_3 = { class: "grid grid-cols-1 gap-4 md:grid-cols-3" }
const _hoisted_4 = { key: 0 }
const _hoisted_5 = {
  for: "roles",
  class: "mb-2 block text-sm font-medium text-gray-800"
}
const _hoisted_6 = {
  key: 1,
  class: "hidden md:block"
}
const _hoisted_7 = {
  key: 4,
  class: "hidden md:block"
}
const _hoisted_8 = { key: 5 }
const _hoisted_9 = {
  for: "levels",
  class: "mb-2 block text-sm font-medium text-gray-800"
}
const _hoisted_10 = {
  for: "levels",
  class: "mb-2 block text-sm font-medium text-gray-800"
}
const _hoisted_11 = { class: "flex flex-col justify-end pb-1.5" }
const _hoisted_12 = { class: "mb-4 text-base font-semibold text-gray-800" }
const _hoisted_13 = { class: "relative grid grid-cols-2 gap-4 lg:grid-cols-4 xl:grid-cols-6" }
const _hoisted_14 = { class: "absolute -right-[215px] bottom-0 mt-6 flex flex-col justify-between rounded border-b-4 border-r-4 border-cyan-600 bg-gray-50 px-4 py-3 text-sm text-gray-600 shadow" }
const _hoisted_15 = {
  key: 0,
  class: "relative mt-5 grid grid-cols-2 gap-4 md:grid-cols-12"
}
const _hoisted_16 = { class: "col-span-3" }
const _hoisted_17 = {
  for: "level",
  class: "mb-2 block text-sm font-medium text-gray-800"
}
const _hoisted_18 = {
  key: 0,
  class: "mt-2"
}
const _hoisted_19 = { class: "mt-4 flex items-center gap-2" }

import { computed, reactive, ref, Ref, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';

import { useUiStore } from '@/stores';
import { can } from '@/composables/useAuth';
import { goBack } from '@/composables/useNavigation';

import {
    containsLowercase,
    containsNumber,
    containsSpecial,
    containsUppercase,
    email,
    maxLength,
    minLength,
    required,
    sameAs,
} from '@/utils/validators';

import { toast, toastServiceError } from '@/services/core/notification';
import { UserService, SelectOptionsService } from '@/services';

import {
    DefaultServiceResult,
    FormAction,
    Permission,
    SelectOptionsServiceGetLevelsResult,
    SelectOptionsServiceGetNumbersResult,
    SelectOptionsServiceGetRolesResult,
    UserDeviceType,
    UserFormState,
    UserModel,
    UserServiceCreateResult,
} from '@/types';

import { UserFormDevice } from '@/views/pages/user/components';
import { DesktopComputerIcon, DeviceMobileIcon } from '@heroicons/vue/solid';
import { InformationCircleIcon } from '@heroicons/vue/solid';
import SVGDeviceExternal from '@/components/svg/SVGDeviceExternal.vue';
import SVGChromeLogo from '@/components/svg/SVGChromeLogo.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'UserFormPage',
  props: {
    action: { type: String, default: 'view' },
},
  setup(__props) {

const route = useRoute();
const { t } = useI18n();
const uiStore = useUiStore();

const props = __props;

const formState = reactive<UserFormState>({
    name: null,
    email: null,
    roles: [],
    levels: [],
    standardLevelId: null,
    isActive: true,
    extensionNumber: null,
    numberId: null,
    useSmartCallerId: false,
    useAutoAttendant: false,
    devices: {
        mobile: false,
        desktop: false,
        web: false,
        external: false,
    },
});

const isLoading = ref(false);
const levelsOptions: Ref<SelectOptionsServiceGetLevelsResult[]> = ref([]);
const numbersOptions: Ref<SelectOptionsServiceGetNumbersResult[]> = ref([]);
const rolesOptions: Ref<SelectOptionsServiceGetRolesResult[]> = ref([]);

const externalConfig = ref();

const noDevice = computed(() => Object.values(formState.devices).every((v) => !v));
const selectedLevels = computed(() =>
    levelsOptions.value.filter((lvl) => formState.levels.includes(lvl.id)),
);

const disableAutoAttendant = computed(
    () => !(formState.devices.mobile || formState.devices.desktop),
);

const autoAttendantTooltip = computed(() => {
    return `<span class='text-base'>${t('user.tooltips.useAutoAttendant')}</span>`;
});

const rules = computed(() => {
    const r = {
        name: {
            required,
            maxLength: maxLength(500),
        },
        email: {
            required,
            email,
            maxLength: maxLength(250),
        },
        roles: { required },
        levels: { required },
        standardLevelId: { required },
        isActive: { required },
        ...(props.action === FormAction.CREATE && {
            password: {
                required,
                minLength: minLength(8),
                maxLength: maxLength(250),
                containsUppercase,
                containsLowercase,
                containsNumber,
                containsSpecial,
            },
            confirmPassword: {
                required,
                sameAsPassword: sameAs(formState.password, t('user.labels.password')),
            },
        }),
    };

    return r;
});

const v$ = useVuelidate(rules, formState);

const hasSmartCallerId = computed(() =>
    formState.standardLevelId ? numbersOptions.value.some((n) => n.isSmartCallerId) : false,
);

const setLoading = (loading: boolean) => {
    isLoading.value = loading;
    uiStore.setIsLoading(loading);
};

const fetchLevels = async () => {
    try {
        const response = await SelectOptionsService.getLevels();
        levelsOptions.value = response.data ?? [];
    } catch (error) {
        toastServiceError(error);
    }
};

const fetchRoles = async () => {
    try {
        const response = await SelectOptionsService.getRoles();
        rolesOptions.value = response.data ?? [];
    } catch (error) {
        toastServiceError(error);
    }
};

const fetchUser = async (id: number) => {
    try {
        const response = await UserService.get<UserModel>(id);
        const {
            name,
            email,
            levels,
            roles,
            isActive,
            standardLevelId,
            numbers,
            extension,
            devices,
            useSmartCallerId,
            useAutoAttendant,
        } = response.data;

        Object.assign(formState, {
            id,
            name,
            email,
            roles: roles?.map((l) => l.id) || [],
            levels: levels?.map((l) => l.id) || [],
            standardLevelId: standardLevelId ?? null,
            isActive,
            extensionNumber: extension?.number || null,
            numberId: numbers?.length ? numbers[0].id : null,
            useSmartCallerId: useSmartCallerId || false,
            useAutoAttendant: useAutoAttendant || false,
            devices: {
                mobile: devices?.some((d) => d.deviceType === UserDeviceType.MOBILE),
                desktop: devices?.some((d) => d.deviceType === UserDeviceType.DESKTOP),
                web: devices?.some((d) => d.deviceType === UserDeviceType.WEB),
                external: devices?.some((d) => d.deviceType === UserDeviceType.EXTERNAL),
            },
        });

        await fetchNumbers();

        const deviceExternal = devices?.find((d) => d.deviceType === UserDeviceType.EXTERNAL);
        if (deviceExternal) {
            externalConfig.value = {
                hostname: deviceExternal.hostname,
                sipUser: deviceExternal.sipUser,
                sipPassword: deviceExternal.sipPassword,
            };
        }
    } catch (error) {
        toastServiceError(error);
    }
};

const fetchNumbers = async () => {
    try {
        if (!formState.standardLevelId) {
            numbersOptions.value = [];
            return;
        }

        const response = await SelectOptionsService.getNumbers({
            params: { level: formState.standardLevelId },
        });
        numbersOptions.value = response.data;
    } catch (error) {
        toastServiceError(error);
    }
};

const onSubmit = async () => {
    const isValid = await v$.value.$validate();
    if (!isValid) return;

    setLoading(true);
    try {
        const id = Number(route.params.id);

        if (!noDevice.value) {
            const isExtensionNumberTaken = await UserService.checkExtensionNumber({
                id: props.action === FormAction.EDIT ? id : null,
                levelId: formState.standardLevelId,
                number: formState.extensionNumber,
            });

            if (isExtensionNumberTaken) {
                throw new Error(t('user.error.extensionUsed'));
            }
        }

        const response =
            props.action === FormAction.EDIT
                ? await UserService.update<DefaultServiceResult, UserFormState>(id, formState)
                : await UserService.create<UserServiceCreateResult, UserFormState>(formState);

        toast.success(response.data.message);
        v$.value.$reset();
        goBack({ name: 'ViewUserById', params: { id } });
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

watch(
    () => formState.levels,
    () => {
        if (formState.standardLevelId && !formState.levels.includes(formState.standardLevelId)) {
            formState.standardLevelId = null;
        }
    },
);

watch(
    () => formState.standardLevelId,
    (newValue) => {
        if (!isLoading.value) {
            formState.numberId = null;
            formState.useSmartCallerId = false;
            if (newValue) fetchNumbers();
        }
    },
);

watch(
    () => formState.useSmartCallerId,
    (newValue) => {
        if (newValue && formState.numberId) formState.numberId = null;
    },
);

watch(
    () => [formState.devices.mobile, formState.devices.desktop],
    () => {
        if (disableAutoAttendant.value && formState.useAutoAttendant) {
            formState.useAutoAttendant = false;
        }
    },
);

onMounted(async () => {
    setLoading(true);
    try {
        const fetchs = [];

        if (can(Permission.ASSIGN_LEVEL_TO_USER)) fetchs.push(fetchLevels());
        if (can(Permission.ASSIGN_ROLE_TO_USER)) fetchs.push(fetchRoles());
        if (props.action === FormAction.EDIT) fetchs.push(fetchUser(Number(route.params.id)));

        await Promise.all(fetchs);
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
});

return (_ctx: any,_cache: any) => {
  const _component_UiBackButton = _resolveComponent("UiBackButton")!
  const _component_UiButton = _resolveComponent("UiButton")!
  const _component_UiPageHeader = _resolveComponent("UiPageHeader")!
  const _component_UiTextInput = _resolveComponent("UiTextInput")!
  const _component_UiMultiselect = _resolveComponent("UiMultiselect")!
  const _component_UiCheckboxInput = _resolveComponent("UiCheckboxInput")!
  const _component_UiPanel = _resolveComponent("UiPanel")!
  const _component_UiCheckDirtyBeforeRouteLeave = _resolveComponent("UiCheckDirtyBeforeRouteLeave")!
  const _directive_tippy = _resolveDirective("tippy")!

  return (_openBlock(), _createElementBlock("form", {
    class: "space-y-4 p-4 sm:p-6 lg:p-8",
    autocomplete: "off",
    onSubmit: _withModifiers(onSubmit, ["prevent"])
  }, [
    _createVNode(_component_UiPageHeader, null, {
      info: _withCtx(() => [
        _createElementVNode("h1", _hoisted_1, _toDisplayString(_ctx.$t('user.' + props.action + '.title')), 1)
      ]),
      actions: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_component_UiBackButton, { fallbackTo: { name: 'ListUser' } }),
          (
                            (props.action == _unref(FormAction).CREATE && _ctx.$can(_unref(Permission).CREATE_USER)) ||
                            (props.action == _unref(FormAction).EDIT && _ctx.$can(_unref(Permission).EDIT_USER))
                        )
            ? (_openBlock(), _createBlock(_component_UiButton, {
                key: 0,
                type: "submit",
                variant: "primary",
                "text-variant": "white"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('core.actions.Save')), 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 1
    }),
    _createVNode(_component_UiPanel, null, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_3, [
          _createVNode(_component_UiTextInput, {
            modelValue: formState.name,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formState.name) = $event)),
            name: "name",
            type: "text",
            label: _ctx.$t('user.labels.name'),
            errors: _unref(v$).name.$errors,
            required: _unref(v$).name.required !== undefined,
            onBlur: _unref(v$).name.$touch
          }, null, 8, ["modelValue", "label", "errors", "required", "onBlur"]),
          _createVNode(_component_UiTextInput, {
            modelValue: formState.email,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formState.email) = $event)),
            name: "email",
            type: "text",
            label: _ctx.$t('user.labels.email'),
            autocomplete: "new-email",
            errors: _unref(v$).email.$errors,
            required: _unref(v$).email.required !== undefined,
            onBlur: _unref(v$).email.$touch
          }, null, 8, ["modelValue", "label", "errors", "required", "onBlur"]),
          (_ctx.$can(_unref(Permission).ASSIGN_ROLE_TO_USER))
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                _createElementVNode("label", _hoisted_5, [
                  _createTextVNode(_toDisplayString(_ctx.$t('user.labels.roles')) + " ", 1),
                  _cache[16] || (_cache[16] = _createElementVNode("span", { class: "text-xs" }, "*", -1))
                ]),
                _createVNode(_component_UiMultiselect, {
                  name: "roles",
                  id: "roles",
                  modelValue: formState.roles,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((formState.roles) = $event)),
                  options: rolesOptions.value,
                  primitive: "",
                  multiple: "",
                  "can-clear": "",
                  errors: _unref(v$).roles?.$errors,
                  label: "name",
                  "value-prop": "id"
                }, null, 8, ["modelValue", "options", "errors"])
              ]))
            : (_openBlock(), _createElementBlock("div", _hoisted_6)),
          (props.action == _unref(FormAction).CREATE)
            ? (_openBlock(), _createBlock(_component_UiTextInput, {
                key: 2,
                modelValue: formState.password,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((formState.password) = $event)),
                name: "password",
                type: "password",
                label: _ctx.$t('user.labels.password'),
                autocomplete: "new-password",
                errors: _unref(v$).password?.$errors,
                required: _unref(v$).password?.required !== undefined,
                onBlur: _unref(v$).password?.$touch
              }, null, 8, ["modelValue", "label", "errors", "required", "onBlur"]))
            : _createCommentVNode("", true),
          (props.action == _unref(FormAction).CREATE)
            ? (_openBlock(), _createBlock(_component_UiTextInput, {
                key: 3,
                modelValue: formState.confirmPassword,
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((formState.confirmPassword) = $event)),
                name: "confirmPassword",
                type: "password",
                label: _ctx.$t('user.labels.confirmPassword'),
                errors: _unref(v$).confirmPassword?.$errors,
                required: _unref(v$).confirmPassword?.required !== undefined,
                onBlur: _unref(v$).confirmPassword?.$touch
              }, null, 8, ["modelValue", "label", "errors", "required", "onBlur"]))
            : _createCommentVNode("", true),
          (props.action == _unref(FormAction).CREATE)
            ? (_openBlock(), _createElementBlock("div", _hoisted_7))
            : _createCommentVNode("", true),
          (_ctx.$can(_unref(Permission).ASSIGN_LEVEL_TO_USER))
            ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                _createElementVNode("label", _hoisted_9, [
                  _createTextVNode(_toDisplayString(_ctx.$t('user.labels.levels')) + " ", 1),
                  _cache[17] || (_cache[17] = _createElementVNode("span", { class: "text-xs" }, "*", -1))
                ]),
                _createVNode(_component_UiMultiselect, {
                  name: "levels",
                  id: "levels",
                  modelValue: formState.levels,
                  "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((formState.levels) = $event)),
                  options: levelsOptions.value,
                  primitive: "",
                  multiple: "",
                  "can-clear": "",
                  errors: _unref(v$).levels?.$errors,
                  label: "name",
                  "value-prop": "id"
                }, null, 8, ["modelValue", "options", "errors"])
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", null, [
            _createElementVNode("label", _hoisted_10, [
              _createTextVNode(_toDisplayString(_ctx.$t('user.labels.standardLevel')) + " ", 1),
              _cache[18] || (_cache[18] = _createElementVNode("span", { class: "text-xs" }, "*", -1))
            ]),
            _createVNode(_component_UiMultiselect, {
              name: "standardLevel",
              id: "standardLevel",
              modelValue: formState.standardLevelId,
              "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((formState.standardLevelId) = $event)),
              options: selectedLevels.value,
              required: _unref(v$).standardLevelId.required !== undefined,
              errors: _unref(v$).standardLevelId?.$errors,
              primitive: "",
              label: "name",
              "value-prop": "id"
            }, null, 8, ["modelValue", "options", "required", "errors"])
          ]),
          _createElementVNode("div", _hoisted_11, [
            _createVNode(_component_UiCheckboxInput, {
              modelValue: formState.isActive,
              "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((formState.isActive) = $event)),
              name: "isActive",
              label: _ctx.$t('user.labels.isActive'),
              class: "h-6"
            }, null, 8, ["modelValue", "label"])
          ])
        ])
      ]),
      _: 1
    }),
    _createVNode(_component_UiPanel, null, {
      default: _withCtx(() => [
        _createElementVNode("h3", _hoisted_12, _toDisplayString(_ctx.$t('user.labels.devices.title')), 1),
        _createElementVNode("div", _hoisted_13, [
          _createVNode(_unref(UserFormDevice), {
            label: _ctx.$t('user.labels.devices.mobile'),
            modelValue: formState.devices.mobile,
            "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((formState.devices.mobile) = $event))
          }, {
            icon: _withCtx(() => [
              _createVNode(_unref(DeviceMobileIcon), { class: "mt-6 h-20 w-20" })
            ]),
            _: 1
          }, 8, ["label", "modelValue"]),
          _createVNode(_unref(UserFormDevice), {
            label: _ctx.$t('user.labels.devices.desktop'),
            modelValue: formState.devices.desktop,
            "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((formState.devices.desktop) = $event))
          }, {
            icon: _withCtx(() => [
              _createVNode(_unref(DesktopComputerIcon), { class: "mt-6 h-20 w-20" })
            ]),
            _: 1
          }, 8, ["label", "modelValue"]),
          _createVNode(_unref(UserFormDevice), {
            label: _ctx.$t('user.labels.devices.web'),
            modelValue: formState.devices.web,
            "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((formState.devices.web) = $event))
          }, {
            icon: _withCtx(() => [
              _createVNode(SVGChromeLogo, { class: "mt-6 h-20 w-20" })
            ]),
            _: 1
          }, 8, ["label", "modelValue"]),
          _createVNode(_unref(UserFormDevice), {
            label: _ctx.$t('user.labels.devices.external'),
            modelValue: formState.devices.external,
            "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((formState.devices.external) = $event))
          }, _createSlots({
            icon: _withCtx(() => [
              _createVNode(SVGDeviceExternal, { class: "mt-6 h-20 w-20" })
            ]),
            _: 2
          }, [
            (externalConfig.value)
              ? {
                  name: "extra-info",
                  fn: _withCtx(() => [
                    _createElementVNode("div", _hoisted_14, [
                      _cache[22] || (_cache[22] = _createElementVNode("h3", { class: "mb-1 text-base font-semibold" }, "Configurações do PABX", -1)),
                      _createElementVNode("p", null, [
                        _cache[19] || (_cache[19] = _createElementVNode("span", { class: "font-semibold" }, "Hostname:", -1)),
                        _createTextVNode(" " + _toDisplayString(externalConfig.value.hostname), 1)
                      ]),
                      _createElementVNode("p", null, [
                        _cache[20] || (_cache[20] = _createElementVNode("span", { class: "font-semibold" }, "Username:", -1)),
                        _createTextVNode(" " + _toDisplayString(externalConfig.value.sipUser), 1)
                      ]),
                      _createElementVNode("p", null, [
                        _cache[21] || (_cache[21] = _createElementVNode("span", { class: "font-semibold" }, "Password:", -1)),
                        _createTextVNode(" " + _toDisplayString(externalConfig.value.sipPassword), 1)
                      ])
                    ])
                  ]),
                  key: "0"
                }
              : undefined
          ]), 1032, ["label", "modelValue"])
        ]),
        (!noDevice.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
              _createVNode(_component_UiTextInput, {
                modelValue: formState.extensionNumber,
                "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((formState.extensionNumber) = $event)),
                name: "extensionNumber",
                type: "number",
                label: _ctx.$t('user.labels.extensionNumber'),
                class: "col-span-3"
              }, null, 8, ["modelValue", "label"]),
              _createElementVNode("div", _hoisted_16, [
                _createElementVNode("label", _hoisted_17, _toDisplayString(_ctx.$t('user.labels.number')), 1),
                _createVNode(_component_UiMultiselect, {
                  name: "number",
                  modelValue: formState.numberId,
                  "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((formState.numberId) = $event)),
                  placeholder: _ctx.$t('core.random'),
                  options: numbersOptions.value,
                  primitive: "",
                  "can-clear": "",
                  disabled: noDevice.value || formState.useSmartCallerId,
                  label: "number",
                  "value-prop": "id"
                }, null, 8, ["modelValue", "placeholder", "options", "disabled"]),
                (hasSmartCallerId.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                      _createVNode(_component_UiCheckboxInput, {
                        modelValue: formState.useSmartCallerId,
                        "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((formState.useSmartCallerId) = $event)),
                        label: _ctx.$t('user.labels.useSmartCallerId'),
                        name: "useSmartCallerId"
                      }, null, 8, ["modelValue", "label"])
                    ]))
                  : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_19, [
                  _createVNode(_component_UiCheckboxInput, {
                    modelValue: formState.useAutoAttendant,
                    "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((formState.useAutoAttendant) = $event)),
                    label: _ctx.$t('user.labels.useAutoAttendant'),
                    name: "useAutoAttendant",
                    disabled: disableAutoAttendant.value
                  }, null, 8, ["modelValue", "label", "disabled"]),
                  _withDirectives(_createVNode(_unref(InformationCircleIcon), {
                    class: "h-5 w-5 text-gray-400",
                    "aria-hidden": "true"
                  }, null, 512), [
                    [_directive_tippy, { content: autoAttendantTooltip.value, allowHTML: true }]
                  ])
                ])
              ])
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    _createVNode(_component_UiCheckDirtyBeforeRouteLeave, {
      dirty: _unref(v$).$anyDirty
    }, null, 8, ["dirty"])
  ], 32))
}
}

})